import React, { useEffect, useState, createContext, useContext, useRef } from "react";
import { ChevronDown, Check, Circle } from "lucide-react";
import { sendRequest } from "../../api";
import { useUserContext } from "../UserProvider";

const CategoryContext = createContext(null);

// Function to capitalize the first letter of each word
const capitalizeWords = (str) => {
  return str.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
};

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [enabledItems, setEnabledItems] = useState({});

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const fetchedCategories = await sendRequest("/users/categories");
      setCategories(fetchedCategories);

      // Initialize expanded and enabled states for fetched categories
      const initialExpandedState = {};
      const initialEnabledState = {};

      fetchedCategories.forEach(category => {
        initialExpandedState[category.name] = true;

        // Initialize enabled state for each category and its publishers
        initialEnabledState[category.name] = {
          ...category.publishers.reduce((acc, pub) => ({
            ...acc,
            [pub.name]: pub.enabled || false
          }), {}),
          all: category.publishers.every(pub => pub.enabled)
        };
      });

      setEnabledItems(initialEnabledState);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  return (
    <CategoryContext.Provider value={{
      categories,
      setCategories,
      enabledItems,
      setEnabledItems,
      fetchCategories,
    }}>
      {children}
    </CategoryContext.Provider>
  );
};

export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategory must be used within a CategoryProvider');
  }
  return context;
};

export const CategoryPublisherList = ({ isLoading }) => {
  const { categories, enabledItems, setEnabledItems } = useCategory();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUpdating, setIsUpdating] = useState({});
  const [savedStates, setSavedStates] = useState({});
  const savedTimeoutsRef = useRef({});

  useEffect(() => {
    if (categories.length > 0) {
      setExpandedCategories(categories.reduce((acc, cat) => {
        acc[cat.name] = cat.publishers.some(pub => pub.enabled)
        return acc
      }, {}))
    }

    return () => {
      // Cleanup timeouts on unmount
      Object.values(savedTimeoutsRef.current).forEach(timeout => {
        clearTimeout(timeout);
      });
    };
  }, [categories])

  const isCategoryIndeterminate = (categoryName) => {
    const categoryState = enabledItems[categoryName];
    if (!categoryState) return false;

    const publishers = categories.find(cat => cat.name === categoryName)?.publishers || [];
    const selectedCount = publishers.reduce((count, pub) =>
      categoryState[pub.name] ? count + 1 : count, 0
    );

    return selectedCount > 0 && selectedCount < publishers.length;
  };

  const updateServer = async (categoryName, updates) => {
    setIsUpdating(prev => ({ ...prev, [categoryName]: true }));
    try {
      await sendRequest(`/publishers`, 'PUT', updates)
      setErrorMessage(null);
    } catch (error) {
      console.error("Error updating categories:", error);
      setErrorMessage("Failed to update preferences. Please try again.");
    } finally {
      setIsUpdating(prev => ({ ...prev, [categoryName]: false }));
    }
  }

  const toggleExpandedCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const showSavedIndicator = (categoryName, newsletterName) => {
    const key = newsletterName ? `${categoryName}-${newsletterName}` : categoryName;
    
    // Clear existing timeout if any
    if (savedTimeoutsRef.current[key]) {
      clearTimeout(savedTimeoutsRef.current[key]);
    }

    // Show saved indicator
    setSavedStates(prev => ({ ...prev, [key]: true }));

    // Set timeout to hide it
    savedTimeoutsRef.current[key] = setTimeout(() => {
      setSavedStates(prev => ({ ...prev, [key]: false }));
      delete savedTimeoutsRef.current[key];
    }, 2000);
  };

  const toggleEnableAll = async (categoryName) => {
    setEnabledItems(prev => {
      const isCurrentlyEnabled = prev[categoryName]?.all || false;
      const newState = !isCurrentlyEnabled;

      // Create a new object for the category if it doesn't exist
      const categoryState = prev[categoryName] || {};

      // Set all publishers to the new state
      const updatedPublishers = categories
        .find(cat => cat.name === categoryName)
        ?.publishers.reduce((acc, publisher) => {
          acc[publisher.name] = newState;
          return acc;
        }, {});

      // auto-open
      setExpandedCategories(prevExpanded => ({
        ...prevExpanded,
        [categoryName]: true,
      }));

      updateServer(categoryName, {
        publishers: updatedPublishers
      });

      showSavedIndicator(categoryName);

      return {
        ...prev,
        [categoryName]: {
          ...categoryState,
          all: newState,
          ...updatedPublishers,
        },
      };
    });
  };

  const toggleNewsletter = (categoryName, newsletterName) => {
    if (isUpdating[categoryName]) return;

    setEnabledItems(prev => {
      const categoryState = prev[categoryName] || {};
      const newNewsletterState = !categoryState[newsletterName];

      // Update the newsletter state
      const updatedCategoryState = {
        ...categoryState,
        [newsletterName]: newNewsletterState,
      };

      // Check if all newsletters are enabled after the update
      const allNewsletters = categories.find(cat => cat.name === categoryName)?.publishers || [];
      const allEnabled = allNewsletters.every(newsletter =>
        newsletter.name === newsletterName
          ? newNewsletterState
          : updatedCategoryState[newsletter.name]
      );

      // Update the 'all' state based on whether all newsletters are enabled
      updatedCategoryState.all = allEnabled;

      updateServer(categoryName, {
        publishers: {
          [newsletterName]: newNewsletterState
        }
      });

      showSavedIndicator(categoryName, newsletterName);

      return {
        ...prev,
        [categoryName]: updatedCategoryState,
      };
    });
  };

  return (
    <div className="w-full max-w-md mx-auto pt-4 p-2 space-y-3">
      <div className="space-y-3">
        {errorMessage && (
          <div className="bg-red-50 text-red-600 p-2 mb-3 rounded text-sm">
            {errorMessage}
          </div>
        )}

        {categories.map((category, i) => (
          <div
            key={`${category.name}-${i}`}
            className="rounded-lg overflow-hidden border border-solid border-primary-100 shadow-sm category-fade-in"
          >
            <div className="flex hover:bg-gray-100 transition-colors duration-300">
              <div className="py-4 px-3 ml-2 flex items-center">
                <input
                  type="checkbox"
                  checked={enabledItems[category.name]?.all || false}
                  ref={input => {
                    if (input) {
                      input.indeterminate = isCategoryIndeterminate(category.name);
                    }
                  }}
                  onChange={() => toggleEnableAll(category.name)}
                  className="w-4 h-4 rounded flex-shrink-0 border-gray-300 accent-[#14997e] checked:bg-primary checked:hover:bg-primary-dark focus:ring-primary focus:ring-offset-0"
                />
              </div>
              <button
                onClick={() => toggleExpandedCategory(category.name)}
                className="flex-1 py-4 pr-3 flex items-center justify-between"
              >
                <div className="flex items-center space-x-2">
                  <div
                    className="w-6 h-6 rounded-full flex flex-shrink-0 items-center justify-center"
                    style={{ backgroundColor: category.color }}
                  >
                    <span className="text-white text-[12px] font-medium">
                      {capitalizeWords(category.name[0])}
                    </span>
                  </div>
                  <span className="line-clamp-2 text-[14px] text-gray-900 tracking-wide">{capitalizeWords(category.name)}</span>
                </div>
                <div className="flex items-center">
                  {savedStates[category.name] && (
                    <div className="mr-2 text-primary animate-fade-in flex items-center">
                      <div className="relative w-4 h-4">
                        <Circle className="w-4 h-4 absolute animate-circle-out" strokeWidth={2} />
                        <Check className="w-4 h-4 absolute animate-check-in" strokeWidth={2} />
                      </div>
                    </div>
                  )}
                  <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expandedCategories[category.name] ? 'rotate-180' : ''}`} />
                </div>
              </button>
            </div>

            <div
              className={`category-content overflow-hidden ${expandedCategories[category.name] ? 'category-content-enter category-content-enter-active' : 'category-content-exit category-content-exit-active'}`}
            >
              <div className="border-t border-gray-100">
                <div className="divide-y divide-gray-100">
                  {category.publishers.map((newsletter) => (
                    <div
                      key={newsletter.name}
                      className="px-4 py-3 flex items-center justify-between bg-white hover:bg-gray-50 pl-4 cursor-pointer newsletter-item"
                      onClick={() => toggleNewsletter(category.name, newsletter.name)}
                    >
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={enabledItems[category.name]?.[newsletter.name] || false}
                          className="w-3 h-3 rounded flex-shrink-0 border-gray-300 accent-[#14997e] checked:bg-primary checked:hover:bg-primary-dark focus:ring-primary focus:ring-offset-0"
                          onChange={() => { }}
                        />
                        <span className="text-[13px] font-light text-gray-600 tracking-wider">{capitalizeWords(newsletter.name)}</span>
                      </div>
                      {savedStates[`${category.name}-${newsletter.name}`] && (
                        <div className="text-primary animate-fade-in flex items-center">
                          <div className="relative w-4 h-4">
                            <Circle className="w-4 h-4 absolute animate-circle-out" strokeWidth={2} />
                            <Check className="w-4 h-4 absolute animate-check-in" strokeWidth={2} />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CategoryPublisherList;
